import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { homeForRole } from "@/lib/authUtils";
import BrandLoader from "@/components/feature/BrandLoader";
import BrandLogo from "@/components/feature/BrandLogo";

function AccessNotice({
  title,
  message,
  onSignOut,
}: {
  title: string;
  message: string;
  onSignOut: () => void;
}) {
  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background-50 px-6 text-center">
      <BrandLogo size={92} />
      <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-100 p-6">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className="ri-shield-cross-line text-2xl"></i>
        </div>
        <h1 className="font-heading text-lg font-semibold text-foreground-950">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{message}</p>
        <button
          type="button"
          onClick={onSignOut}
          className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-logout-box-r-line text-base"></i>
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

interface RequireAuthProps {
  children: ReactNode;
  role?: "admin" | "cashier";
}

export function RequireAuth({ children, role }: RequireAuthProps) {
  const { session, profile, roleName, loading, signOut } = useAuth();
  const location = useLocation();

  if (loading) return <BrandLoader label="Verificando sesión..." />;

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!profile) {
    return (
      <AccessNotice
        title="Acceso no configurado"
        message="Tu cuenta no tiene un perfil asignado en el sistema. Contacta al administrador."
        onSignOut={signOut}
      />
    );
  }

  if (!profile.active) {
    return (
      <AccessNotice
        title="Usuario inactivo"
        message="Tu usuario ha sido desactivado. Contacta al administrador para reactivar tu acceso."
        onSignOut={signOut}
      />
    );
  }

  if (role === "admin" && roleName !== "admin") {
    return <Navigate to={homeForRole(roleName)} replace />;
  }

  if (role === "cashier" && roleName !== "cashier") {
    return <Navigate to={homeForRole(roleName)} replace />;
  }

  return <>{children}</>;
}

export function GuestOnly({ children }: { children: ReactNode }) {
  const { session, profile, roleName, loading, signOut } = useAuth();

  if (loading) return <BrandLoader label="Cargando..." />;

  if (session && !profile) {
    return (
      <AccessNotice
        title="Acceso no configurado"
        message="Tu cuenta no tiene un perfil asignado en el sistema. Contacta al administrador."
        onSignOut={signOut}
      />
    );
  }

  if (session) {
    return <Navigate to={homeForRole(roleName)} replace />;
  }

  return <>{children}</>;
}